import type { Building } from "building-types/building/buildingTypes";
import type { ComponentTransform } from "building-types/components/componentTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { EdgeModifier } from "building-types/modifiers/edgeModifierTypes";
import type { GeneratedModifier } from "building-types/modifiers/generatedModifier";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { Railing, PostPlacement, RailingLinework } from "building-types/settings/railingTypes";
import { DefaultPost } from "building-types/settings/railingTypes";
import type {
  GeneratedRailing,
  PostPlacementInstance,
  RailingDefaultSetting,
  RailingGeomGroup,
  RailingGeomTag,
} from "building-types/settings/railingTypes";
import { RailingInfillStyle, RailingSettingsType } from "building-types/settings/railingTypes";
import { RailingViewNoteType } from "building-types/settings/viewNotesSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { RWTable, Table } from "common/data/table";
import type { MaterialSrc } from "common/materials/materialSrc";
import deepmerge from "common/utils/deepmerge";
import { worldInchesNew } from "common/utils/nominalNumbers";
import uid from "common/utils/uid";
import { railingGetCleanPath, railingMirror } from "doc/elements/railing/path";
import { postPointsFromPostPlacements } from "doc/elements/railing/postPlacements";
import { STAIR_CUT_HEIGHT } from "doc/elements/stairSystem/stairConstants";
import {
  ARCHETYPE_INSTANCE_CHAIN_KEY,
  composeComponentInstanceChain,
  EMPTY_INSTANCE_CHAIN,
  instanceIdForComponent,
} from "doc/entity/componentInstanceEntityResolve";
import { docEntityTableFilter } from "doc/entity/docEntityTableFilter";
import { evalFormulas, evaluateEntityForComponentInstance, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { materialSrcFromPalette } from "doc/materialLookup";
import { buildEdgeModifiersByParentId, getEntityEdgeModifiersTable, polyLine2Modify } from "doc/utils/modifierUtils";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { facesByPolyline3Segments, type Extrusion, type Face } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import GeomTagGroup from "geom/geomTagGroup";
import { polyLine2Thicken } from "geom/offset";
import { edgeIdsByPolyLine2, polyLine2Copy, polyLine2EdgeCount, polyLine2Extend } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3ByTwoPoints, polyLine3EdgeCount, type PolyLine3 } from "geom/polyLine3";
import { shatterPolyLine2 } from "../elements/porch/porchUtils";
import { makeInfill } from "../elements/railing/infill";
import { getRailingLineworkWidth, makeRailingLinework2 } from "../elements/railing/linework";
import { makePostsByPlacements, makePostsBySettings, postRemovalTableNew } from "../elements/railing/posts";
import { getRailThickness, makeBottomRail, makeTopRail } from "../elements/railing/rails";
import {
  makeLevelFloorAndStairSurfaces,
  makePolyLine3OnFloorAndStairSurfaces,
  type Path3OnSurfaces,
} from "../elements/railing/surfaces";
import { componentInstanceLevelId } from "../generationTools/components/componentInstanceLevel";
import type { RailingGenerationContext } from "../types/railingGenerationTypes";
import { applyTransformToOutline, componentInstanceMat3ForMirroredTemplate } from "../utils/componentTransforms";
import { shouldGenerateEntity } from "../utils/entityIsolation";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";
import { uvGenerationForInfill } from "../utils/uvGenerationForEntities";

function select({
  doc: { edgeModifiers, optionSets, instanceOptionSets, railings, railingPosts: posts },
  optionChoices: buildingOptionChoices,
  mirror,
  mirrorTranslationX,
  modifiersByInstanceChain,
  levels,
  settings,
  stairSystems,
  rooms,
  roomsByLevel,
  roomFloorAssemblies,
  entityDisabledByGroup,
  floorSystems,
  idToParents,
  componentToTransforms,
  enabledFeatures,
  isolatedEntity,
  exteriorPalette,
}: Pick<
  Building,
  | "doc"
  | "optionChoices"
  | "mirror"
  | "mirrorTranslationX"
  | "modifiersByInstanceChain"
  | "levels"
  | "settings"
  | "stairSystems"
  | "rooms"
  | "roomsByLevel"
  | "roomFloorAssemblies"
  | "entityDisabledByGroup"
  | "floorSystems"
  | "idToParents"
  | "componentToTransforms"
  | "enabledFeatures"
  | "isolatedEntity"
  | "exteriorPalette"
>) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    docRailings: railings,
    docPosts: posts,
    edgeModifiers,
    rooms,
    mirror,
    mirrorTranslationX,
    modifiersByInstanceChain,
    optionChoices: resolveOptionsInFormula(railings, definitions, buildingOptionChoices),
    rawOptionChoices: buildingOptionChoices,
    definitions,
    levels,
    railingSettings: settings.railing,
    stairSystems,
    roomsByLevel,
    roomFloorAssemblies,
    entityDisabledByGroup,
    floorSystems,
    idToParents,
    componentToTransforms,
    enabledFeatures,
    isolatedEntity,
    exteriorPalette,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  rawOptionChoices: "shallow",
  definitions: "shallow",
  edgeModifiers: "deep",
});

export interface GenerateSingleRailingResult {
  groups: RailingGeomGroup[];
  railingLinework2: { [levelId: string]: RailingLinework };
  railingPathEdge3s: Path3OnSurfaces[];
  resolvedPath3s: (PolyLine3 | undefined)[];
  postPlacementInstances: PostPlacementInstance[];
  railingPathFacesForBuildingOutlineAndLighting: Face[];
  buildingOutlineExtrusions: Extrusion[];
  issues: BuildingGenerationIssue[];
  terminationLogicWouldChange: boolean;
}

export interface ComponentRailingGenerationContext {
  readonly docRailing: Railing;
  readonly docPosts: Table<PostPlacement>;
  readonly settings: RailingDefaultSetting;
  readonly context: RailingGenerationContext;
  /** The authored path, before any modifier or mirror is applied. */
  readonly originalPath: PolyLine2;
  readonly edgeIds: string[];
  readonly railingEdgeModifiers: Table<EdgeModifier>;
  readonly modifiersByInstanceChain: Table<Table<GeneratedModifier>>;
  readonly postHalfWidth: number;
  readonly lineworkWidth: number;
}

export function generateSingleRailing(
  railingId: string,
  railing: Railing,
  docPosts: Table<PostPlacement>,
  path: PolyLine2,
  settings: RailingDefaultSetting,
  context: RailingGenerationContext,
  defaultMaterialSrc: MaterialSrc | undefined,
  pickHandle?: DocEntityHandle,
): GenerateSingleRailingResult {
  const {
    levels,
    levelRoomSurfaceRTree,
    levelRoomSurfaces,
    levelStairSurfaceRTree,
    levelStairSurfaces,
    levelPerimeterSurfaceRTree,
    levelPerimeterSurfaces,
  } = context;

  const issues: BuildingGenerationIssue[] = [];
  const groups: RailingGeomGroup[] = [];

  const railingHandle: DocEntityHandle = { type: "RAILING", id: railingId };
  const generalGroup: RailingGeomGroup = {
    group: new GeomTagGroup<RailingGeomTag>(),
    pickHandle: pickHandle ?? railingHandle,
  };

  const {
    hasTopRail,
    topRailHeight = 0,
    topRail,
    topRailMaterial,
    hasBottomRail,
    bottomRailHeight = 0,
    bottomRail,
    bottomRailMaterial,
    hasPosts,
    removeStartPost,
    removeEndPost,
    postHeight,
    post,
    postMaterial,
    infillStyle,
    infillDepth,
    baluster,
    infillMaterial,
    removeTransitionPosts,
  } = settings;
  const resolvedTopRailMaterial = topRailMaterial ?? defaultMaterialSrc;
  const resolvedBottomRailMaterial = bottomRailMaterial ?? defaultMaterialSrc;
  const resolvedPostMaterial = postMaterial ?? defaultMaterialSrc;
  const resolvedInfillMaterial = infillMaterial ?? defaultMaterialSrc;

  const railingViewEntityDescriptor = viewEntityDescriptorNew(
    uid(),
    ViewEntityType.RAILING,
    settings.viewNotes?.[RailingViewNoteType.RAILING],
  );

  const railingPathEdge2s = shatterPolyLine2(polyLine2Copy(path));
  const postHalfWidth = post ? post.width / 2 : 0;
  const { levelId } = railing;
  const levelIsSet = levelId?.length > 0;

  if (!levelIsSet) {
    issues.push({
      type: BuildingGenerationIssueType.RAILING_LEVEL_UNDEFINED,
      handle: {
        type: "RAILING",
        id: railingId,
      },
    });
  }

  const level = levels[levelId];
  if (levelIsSet && !level) {
    return {
      groups: [],
      railingLinework2: {},
      railingPathEdge3s: [],
      resolvedPath3s: [],
      postPlacementInstances: [],
      railingPathFacesForBuildingOutlineAndLighting: [],
      buildingOutlineExtrusions: [],
      issues,
      terminationLogicWouldChange: false,
    };
  }

  const railingPathEdge3s =
    levelIsSet && level
      ? makePolyLine3OnFloorAndStairSurfaces({
          path2: railingPathEdge2s,
          levelId,
          levels,
          levelRoomSurfaceRTree,
          levelRoomSurfaces,
          levelStairSurfaceRTree: railing.ignoreStairsAndRoomsAbove ? {} : levelStairSurfaceRTree,
          levelStairSurfaces: railing.ignoreStairsAndRoomsAbove ? {} : levelStairSurfaces,
          levelPerimeterSurfaceRTree,
          levelPerimeterSurfaces,
          useNewRailingTerminationLogic: railing.useNewRailingTerminationLogic ?? false,
        })
      : [];

  let postPlacementInstances: PostPlacementInstance[] = [];
  const railingPathFacesForBuildingOutlineAndLighting: Face[] = [];
  const buildingOutlineExtrusions: Extrusion[] = [];
  const resolvedPath3s = railingPathEdge3s.flatMap(({ polyLine3 }): (PolyLine3 | undefined)[] => {
    if (!polyLine3) {
      return [undefined];
    }

    const segments: PolyLine3[] = [];
    for (let i = 0; i < polyLine3EdgeCount(polyLine3); i++) {
      const offset = i * 3;
      segments.push(
        polyLine3ByTwoPoints(
          polyLine3[offset],
          polyLine3[offset + 1],
          polyLine3[offset + 2],
          polyLine3[offset + 3],
          polyLine3[offset + 4],
          polyLine3[offset + 5],
        ),
      );
    }
    return segments;
  });

  const { newTable: postPlacements } = docEntityTableFilter(docPosts, (x) => x.parentId === railing.id);

  if (railingPathEdge3s.length && post) {
    const topRailThicknessResolved = getRailThickness(topRail);
    const bottomRailThicknessResolved = getRailThickness(bottomRail);

    if (hasPosts && typeof postHeight === "number") {
      const edgeCount = polyLine2EdgeCount(path);
      postPlacementInstances = postPointsFromPostPlacements(postPlacements, edgeCount, path);
      const postsToRemoveByEdgeIndex = postRemovalTableNew(postPlacements, edgeCount);

      makePostsByPlacements({
        postPlacementInstances, // reads from this field to add columns from placements (user-defined)
        railingPathEdge2s,
        railingPathEdge3s,
        level,
        levels,
        railing,
        levelStairSurfaceRTree,
        levelStairSurfaces,
        groups,
        postHeight,
        topRailThicknessResolved,
        post,
        postMaterial: resolvedPostMaterial,
        railingViewEntityDescriptor,
        levelPerimeterSurfaceRTree,
        levelPerimeterSurfaces,
      });

      makePostsBySettings({
        postPlacementInstances, // writes to this field to add columns from settings
        group: generalGroup,
        railingPathEdge3s,
        topRailThicknessResolved,
        post,
        postHeight,
        removeStartPost,
        removeEndPost,
        removeTransitionPosts,
        materialSrc: resolvedPostMaterial,
        viewEntityDescriptor: railingViewEntityDescriptor,
        edgeIndexRemovePosts: postsToRemoveByEdgeIndex,
        buildingOutlineExtrusions,
      });

      // we've created the posts above, but now we need to sort them for controlling infill placement
      postPlacementInstances.sort((a, b) => a.edgeIndex - b.edgeIndex || a.param - b.param);
    }

    if (hasTopRail && topRail) {
      if (topRailThicknessResolved > DIST_EPSILON) {
        makeTopRail({
          group: generalGroup,
          railingPathEdge3s,
          topRail,
          topRailThicknessResolved,
          topRailHeight,
          materialSrc: resolvedTopRailMaterial,
          viewEntityDescriptor: railingViewEntityDescriptor,
          postHalfWidth,
        });
      }
    }

    if (hasBottomRail && bottomRail) {
      if (bottomRailThicknessResolved > DIST_EPSILON) {
        makeBottomRail({
          group: generalGroup,
          railingPathEdge3s,
          bottomRail,
          bottomRailThicknessResolved,
          bottomRailHeight,
          materialSrc: resolvedBottomRailMaterial,
          viewEntityDescriptor: railingViewEntityDescriptor,
          postHalfWidth,
        });
      }
    }

    if (infillStyle === RailingInfillStyle.BALUSTERS || infillStyle === RailingInfillStyle.SOLID) {
      if (infillDepth === undefined) {
        throw "infillDepth is undefined";
      }

      const infillGeoms = makeInfill({
        railingPathEdge3s,
        infillDepth: worldInchesNew(infillDepth),
        baluster,
        topRailHeight: worldInchesNew(topRailHeight),
        topRailThicknessResolved,
        bottomRailHeight: worldInchesNew(bottomRailHeight),
        bottomRailThicknessResolved,
        postPlacementInstances,
        postHalfWidth,
        railing,
        level,
        levels,
        levelStairSurfaceRTree,
        levelStairSurfaces,
        levelPerimeterSurfaceRTree,
        levelPerimeterSurfaces,
        infillStyle,
      });

      for (const { geom, segmentIndex } of infillGeoms) {
        const infillTag: RailingGeomTag = {
          type: "INFILL",
          infillType: infillStyle,
          segmentIndex,
          materialSrc: resolvedInfillMaterial,
          uvGen: uvGenerationForInfill(),
          viewEntityDescriptor: railingViewEntityDescriptor,
        };
        generalGroup.group.add(geom, infillTag);
      }
    }
  }

  groups.push(generalGroup);

  const lineworkWidth = getRailingLineworkWidth(settings);

  const railingCutHeight = STAIR_CUT_HEIGHT + topRailHeight - 3;
  const railingLinework2 = makeRailingLinework2({
    railingPathEdge3s,
    levelId,
    levels,
    topRailHeight,
    railingCutHeight,
    lineworkWidth,
    postPlacementInstances,
    post: post ?? DefaultPost,
  });

  // Take the railing edges into account when computing the building outline. These are also used to determine if the railing is inside or outside the building for lighting purposes.
  for (const railingPathEdge of railingPathEdge3s) {
    const polyline3 = railingPathEdge.polyLine3;
    if (polyline3 && polyline3.length >= 6) {
      const WIDTH = 1; // Arbitrary small-ish value
      railingPathFacesForBuildingOutlineAndLighting.push(...facesByPolyline3Segments(polyline3, WIDTH));
    }
  }

  return {
    groups,
    railingLinework2,
    railingPathEdge3s,
    resolvedPath3s,
    postPlacementInstances,
    railingPathFacesForBuildingOutlineAndLighting,
    buildingOutlineExtrusions,
    issues,
    terminationLogicWouldChange: railingPathEdge3s.some((edge) => edge.terminationLogicWouldChange),
  };
}

interface RailingPaths {
  readonly path: PolyLine2;
  readonly pathThickened: PolyLine2;
}

const NO_MODIFIERS: Table<GeneratedModifier> = Object.freeze({});

/**
 * Builds the railing paths for one component instance chain: applies the edge modifiers active for
 * that chain, then the global mirror. The empty chain yields the archetype paths. Returns `undefined`
 * when the mirrored path comes out empty.
 */
function railingPathsForChain(
  params: ComponentRailingGenerationContext,
  componentInstanceChain: readonly string[],
  mirror: boolean,
  mirrorTranslationX: number,
): RailingPaths | undefined {
  const { originalPath, edgeIds, railingEdgeModifiers, modifiersByInstanceChain, postHalfWidth, lineworkWidth } =
    params;

  // Chains the modifiers stage did not cover fall back to the archetype world, and stage sets that
  // omit the stage entirely have no table at all.
  const modifiers =
    modifiersByInstanceChain[composeComponentInstanceChain(componentInstanceChain)] ??
    modifiersByInstanceChain[ARCHETYPE_INSTANCE_CHAIN_KEY] ??
    NO_MODIFIERS;

  const path = railingMirror(
    polyLine2Modify(originalPath, railingEdgeModifiers, modifiers, edgeIds),
    mirror,
    mirrorTranslationX,
  );
  if (!path) {
    return undefined;
  }

  return {
    path,
    pathThickened: polyLine2Thicken(
      polyLine2Extend(polyLine2Copy(path), postHalfWidth, postHalfWidth),
      lineworkWidth,
      true,
    ),
  };
}

function generate({
  docRailings,
  docPosts,
  edgeModifiers,
  mirror,
  mirrorTranslationX,
  modifiersByInstanceChain,
  optionChoices,
  rawOptionChoices,
  definitions,
  levels,
  railingSettings,
  stairSystems,
  rooms,
  roomFloorAssemblies,
  entityDisabledByGroup,
  floorSystems,
  idToParents,
  componentToTransforms,
  enabledFeatures,
  isolatedEntity,
  exteriorPalette,
}: ReturnType<typeof select>): {
  railings: Table<GeneratedRailing>;
  issues: BuildingGenerationIssue[];
} {
  const railings: RWTable<GeneratedRailing> = {};
  const issues: BuildingGenerationIssue[] = [];
  const edgeModifiersByParentId = buildEdgeModifiersByParentId(edgeModifiers);

  // Use the rooms and stair systems that are present in the building.
  const effectiveRooms = rooms;
  const effectiveStairSystems = stairSystems;

  // Build a local rooms-by-level map.
  const roomsByLevelMap: RWTable<string[]> = {};
  for (const roomId in effectiveRooms) {
    const room = effectiveRooms[roomId];
    const levelId = room.level.id;
    let list = roomsByLevelMap[levelId];
    if (!list) {
      list = [];
      roomsByLevelMap[levelId] = list;
    }
    list.push(roomId);
  }

  const {
    levelRoomSurfaceRTree,
    levelRoomSurfaces,
    levelStairSurfaceRTree,
    levelStairSurfaces,
    levelPerimeterSurfaceRTree,
    levelPerimeterSurfaces,
  } = makeLevelFloorAndStairSurfaces(
    levels,
    effectiveRooms,
    roomsByLevelMap,
    roomFloorAssemblies,
    effectiveStairSystems,
    floorSystems,
  );

  const defaultMaterialSrc = materialSrcFromPalette(exteriorPalette, "trimExterior0");
  const railingContext: RailingGenerationContext = {
    levels,
    levelRoomSurfaceRTree,
    levelRoomSurfaces,
    levelStairSurfaceRTree,
    levelStairSurfaces,
    levelPerimeterSurfaceRTree,
    levelPerimeterSurfaces,
  };

  const evaluatedRailings = evalFormulas(docRailings, (v) => optionChoices[v], undefined, entityDisabledByGroup);

  for (const railingId in evaluatedRailings) {
    const railing = evaluatedRailings[railingId];
    const rawDocRailing = docRailings[railingId];
    if (!rawDocRailing) {
      continue;
    }
    if (railing.disabled && !enabledFeatures.modelComponents) {
      continue;
    }

    const originalPath = railingGetCleanPath(railing);
    if (!originalPath) {
      continue;
    }

    const railingEdgeModifiers = getEntityEdgeModifiersTable(edgeModifiersByParentId, railingId);
    const edgeIds = edgeIdsByPolyLine2(originalPath);

    const railingSetting = railingSettings.railingSettingsTable[railing?.railingSettingId];
    if (!railingSetting) {
      continue;
    }
    const entityRailingSetting = railing.settingsOverride;
    const settings = (
      entityRailingSetting ? deepmerge(railingSetting, entityRailingSetting) : railingSetting
    ) as RailingDefaultSetting;
    if (!settings || settings.type !== RailingSettingsType.RAILING_DEFAULT) {
      continue;
    }

    const isSourceVisible = shouldGenerateEntity(railingId, idToParents, isolatedEntity);

    const { post } = settings;
    if (!post) {
      continue;
    }

    // Calculate metadata needed for both source and instances.
    const postHalfWidth = post.width / 2;
    const lineworkWidth = getRailingLineworkWidth(settings);

    const generationParams: ComponentRailingGenerationContext = {
      docRailing: railing,
      docPosts,
      settings,
      context: railingContext,
      originalPath,
      edgeIds,
      railingEdgeModifiers,
      modifiersByInstanceChain,
      postHalfWidth,
      lineworkWidth,
    };

    // The archetype is the empty instance chain, so it goes through the same path builder as instances.
    const archetypePaths = railingPathsForChain(generationParams, EMPTY_INSTANCE_CHAIN, mirror, mirrorTranslationX);
    if (!archetypePaths) {
      continue;
    }
    const { path, pathThickened } = archetypePaths;

    // Generate geometry if source is visible.
    let railingResult: GenerateSingleRailingResult = {
      groups: [],
      railingLinework2: {},
      railingPathEdge3s: [],
      resolvedPath3s: [],
      postPlacementInstances: [],
      railingPathFacesForBuildingOutlineAndLighting: [],
      buildingOutlineExtrusions: [],
      issues: [],
      terminationLogicWouldChange: false,
    };
    let associatedWithStairs = false;

    const shouldGenerateArchetypeRailingGeometry = isSourceVisible && !railing.disabled;

    if (shouldGenerateArchetypeRailingGeometry) {
      railingResult = generateSingleRailing(
        railingId,
        railing,
        docPosts,
        path,
        settings,
        railingContext,
        defaultMaterialSrc,
      );
      issues.push(...railingResult.issues);
      associatedWithStairs = railingResult.railingPathEdge3s.some((edge) => edge.associatedWithStairs);
    }

    // Create generatedRailing object (used for both source and instances).
    const generatedRailing: GeneratedRailing = {
      ...railingResult,
      type: settings.type,
      id: railing.id,
      levelId: railing.levelId,
      baseSettingName: railingSetting.name,
      pickHandle: { type: "RAILING", id: railing.id },
      path,
      railWidth: lineworkWidth * 2,
      infillStyle: settings.infillStyle,
      pathThickened,
      associatedWithStairs,
      postPlacements: docEntityTableFilter(docPosts, (x) => x.parentId === railingId).newTable,
      includeRailInBrochurePlan: settings.includeRailInBrochurePlan ?? false,
      includeRailInPlanDrawing: settings.includeRailInPlanDrawing ?? false,
      includeRailInShowRoomPlan: settings.includeRailInShowRoomPlan ?? false,
      includePostsInBrochurePlan: settings.includePostsInBrochurePlan,
      includePostsInPlanDrawing: settings.includePostsInPlanDrawing,
      includePostsInShowRoomPlan: settings.includePostsInShowRoomPlan,
    };

    // Only add to railings table if source is visible and not disabled at archetype (instances may still enable).
    if (shouldGenerateArchetypeRailingGeometry) {
      railings[railingId] = generatedRailing;
    }

    // Generate component instances if enabled.
    if (enabledFeatures.modelComponents) {
      addRailingComponentInstances(
        railingId,
        generatedRailing,
        idToParents,
        componentToTransforms,
        railings,
        isolatedEntity,
        generationParams,
        defaultMaterialSrc,
        mirror,
        mirrorTranslationX,
        rawDocRailing,
        definitions,
        rawOptionChoices,
      );
    }
  }

  return { railings, issues };
}

export default memo(select, generate, compare);

/**
 * A railing placed by a component instance is selected as the instance.
 */
export function getRailingInstancePickHandle(instanceId: string): DocEntityHandle {
  return { type: "COMPONENT_INSTANCE", id: instanceId };
}

/**
 * Loop through all the component instances and add the railing to the component.
 * Transforms the railing's path, groups, and other geometric properties.
 */
export function addRailingComponentInstances(
  id: string,
  generated: GeneratedRailing,
  idToParents: Table<readonly string[]>,
  componentToTransforms: Table<readonly ComponentTransform[]>,
  resultRailings: RWTable<GeneratedRailing>,
  isolatedEntity: DocEntityHandle | undefined,
  generationParams: ComponentRailingGenerationContext,
  defaultMaterialSrc: MaterialSrc | undefined,
  mirror: boolean,
  mirrorTranslationX: number,
  rawDocRailing: Railing,
  definitions: OptionDefinitions,
  rawOptionChoices: OptionChoices,
): void {
  const parentComponents = idToParents[id] || [];
  for (const componentId of parentComponents) {
    const transforms = componentToTransforms[componentId] || [];
    for (const transform of transforms) {
      if (!shouldGenerateEntity(transform.instanceId, idToParents, isolatedEntity)) {
        continue;
      }

      const evaluatedRailing = evaluateEntityForComponentInstance(
        rawDocRailing,
        transform.componentInstanceChain,
        definitions,
        rawOptionChoices,
      );
      if (evaluatedRailing.disabled) {
        continue;
      }

      const instancePaths = railingPathsForChain(
        generationParams,
        transform.componentInstanceChain,
        mirror,
        mirrorTranslationX,
      );
      // Falls back to the archetype paths when the instance path comes out empty.
      const { path: basePath, pathThickened: basePathThickened } = instancePaths ?? generated;

      const pathMat = componentInstanceMat3ForMirroredTemplate(mirror, mirrorTranslationX, transform.matrix);
      const transformedPath = applyTransformToOutline(pathMat, basePath);
      const transformedPathThickened = applyTransformToOutline(pathMat, basePathThickened);

      const instanceRailingId = instanceIdForComponent(id, transform.componentInstanceChain);
      const instanceRailingPickHandle = getRailingInstancePickHandle(transform.instanceId);

      const levelId = componentInstanceLevelId(
        generationParams.docRailing.levelId,
        generationParams.context.levels,
        transform.verticalPlacement,
        transform.componentInstanceChain,
      );

      const instanceDocRailing = {
        ...generationParams.docRailing,
        levelId,
      };

      const railingResult = generateSingleRailing(
        instanceRailingId,
        instanceDocRailing,
        generationParams.docPosts,
        transformedPath,
        generationParams.settings,
        generationParams.context,
        defaultMaterialSrc,
        instanceRailingPickHandle,
      );

      resultRailings[instanceRailingId] = {
        ...generated,
        ...railingResult,
        id: instanceRailingId,
        instanceId: transform.instanceId,
        pickHandle: instanceRailingPickHandle,
        levelId,
        path: transformedPath,
        pathThickened: transformedPathThickened,
      };
    }
  }
}
